import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { RevenueTrendPoint } from '@/api/stripeService';
import { formatCurrency, formatNumber } from '@/lib/format';
import { useChartTheme } from './chartTheme';

interface RevenueTrendChartProps {
  data: RevenueTrendPoint[];
}

interface TooltipPayloadItem {
  payload: RevenueTrendPoint;
}

const RevenueTrendChart = ({ data }: RevenueTrendChartProps) => {
  const t = useChartTheme();
  const color = t.series[0];

  const renderTooltip = ({
    active,
    payload,
    label,
  }: {
    active?: boolean;
    payload?: readonly TooltipPayloadItem[];
    label?: string | number;
  }) => {
    if (!active || !payload?.length) return null;
    const point = payload[0].payload;
    return (
      <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs shadow-md dark:border-gray-700 dark:bg-gray-900">
        <p className="mb-1 font-medium text-gray-900 dark:text-white">{label}</p>
        <p className="text-gray-600 dark:text-gray-300">
          MRR&nbsp;: <span className="font-semibold">{formatCurrency(point.mrr)}</span>
        </p>
        <p className="text-gray-500 dark:text-gray-400">
          Nouveaux clients&nbsp;: {formatNumber(point.newCustomers)}
        </p>
      </div>
    );
  };

  return (
    <ResponsiveContainer width="100%" height={288}>
      <AreaChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
        <defs>
          <linearGradient id="mrrFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.28} />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid vertical={false} stroke={t.grid} strokeDasharray="3 3" />
        <XAxis
          dataKey="month"
          tick={{ fill: t.axis, fontSize: 12 }}
          tickLine={false}
          axisLine={{ stroke: t.grid }}
        />
        <YAxis
          tick={{ fill: t.axis, fontSize: 12 }}
          tickLine={false}
          axisLine={false}
          width={56}
          tickFormatter={(v: number) => formatCurrency(v)}
        />
        <Tooltip content={renderTooltip} cursor={{ stroke: t.axis, strokeDasharray: '3 3' }} />
        <Area
          type="monotone"
          dataKey="mrr"
          stroke={color}
          strokeWidth={2}
          fill="url(#mrrFill)"
          dot={false}
          activeDot={{ r: 4, strokeWidth: 2, stroke: t.surface }}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};

export default RevenueTrendChart;
